/**
 * Phase 24 — Admin-only PWS status panel.
 *
 * Reusable across BookingDetail, LabDetail and PartnerNetwork drawer.
 * Renders the aggregated read-only follow-up / workflow / service-level
 * status surfaced by /api/admin/pws-status-panel/{booking|partner|report}.
 *
 * PINNED USER RULE (Feb 2026):
 *   Admin-only. Read-only. Never sends emails / SMS / notifications.
 *   Wording is operational ("follow-up status", "opportunity workflow",
 *   "service-level status", "open follow-ups", "last activity").
 *   Never punitive ("breach", "penalty", "failed").
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ListChecks, Workflow, Clock, ChevronRight, AlertTriangle, Loader2,
  ShieldAlert, Activity,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";


const SL_BADGE_COLORS = {
  on_track:        "bg-emerald-100 text-emerald-800 border-emerald-200",
  at_risk:         "bg-amber-100 text-amber-800 border-amber-200",
  overdue:         "bg-rose-100 text-rose-800 border-rose-200",
  complete:        "bg-slate-100 text-slate-600 border-slate-200",
  not_applicable:  "bg-slate-50 text-slate-400 border-slate-200",
};


function SLBadge({ status }) {
  const cls = SL_BADGE_COLORS[status] || SL_BADGE_COLORS.not_applicable;
  return (
    <span
      data-testid={`ph24-sl-status-${status}`}
      className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}
    >
      {(status || "—").replace("_", " ")}
    </span>
  );
}


function StatusPill({ label, value, testId, color = "slate" }) {
  if (value === null || value === undefined) return null;
  const palette = {
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
    cyan:    "bg-cyan-50 text-cyan-800 border-cyan-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    rose:    "bg-rose-50 text-rose-800 border-rose-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  }[color] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${palette}`}
    >
      <span className="text-slate-500">{label}:</span> {value}
    </span>
  );
}


function DeepLink({ to, testId, children }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-blue-700 hover:text-blue-800"
    >
      {children} <ChevronRight className="w-3 h-3" />
    </Link>
  );
}


export function PWSStatusPanel({ kind, identifier, isAdmin = false }) {
  const [body, setBody] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAdmin || !identifier) {
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(
          `/admin/pws-status-panel/${kind}/${encodeURIComponent(identifier)}`,
        );
        if (alive) setBody(data);
      } catch (e) {
        if (alive) setError(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [kind, identifier, isAdmin]);

  // Admin-only — render nothing for non-admins (defence-in-depth even
  // though the API also enforces this).
  if (!isAdmin) return null;

  if (loading) {
    return (
      <section
        data-testid="ph24-status-panel-loading"
        className="rounded-2xl bg-white border border-slate-200 p-3 mt-4 flex items-center gap-2 text-xs text-slate-400"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading partner status…
      </section>
    );
  }

  if (error) {
    return (
      <section
        data-testid="ph24-status-panel-error"
        className="rounded-2xl bg-rose-50 border border-rose-200 p-3 mt-4 text-xs text-rose-800 flex items-start gap-2"
      >
        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span>{error}</span>
      </section>
    );
  }

  if (!body) return null;

  const dl = body.deep_links || {};
  const sl = body.service_level_status || {};

  return (
    <section
      data-testid={`ph24-status-panel-${kind}`}
      className="rounded-2xl bg-white border border-slate-200 p-4 mt-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-700 flex items-center gap-1.5">
          <Activity className="w-3 h-3 text-cyan-700" />
          <span data-testid="ph24-panel-heading">PWS status · admin-only</span>
        </div>
        <div className="text-[10px] text-slate-400 italic flex items-center gap-1">
          <ShieldAlert className="w-3 h-3" /> read-only · no notifications sent
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {kind === "booking" && (
          <>
            <StatusPill
              testId="ph24-open-followup-count"
              label="open follow-ups"
              value={body.open_follow_up_count}
              color={body.open_follow_up_count > 0 ? "cyan" : "slate"}
            />
            <StatusPill
              testId="ph24-current-followup-status"
              label="follow-up"
              value={body.current_follow_up_status || "—"}
              color="slate"
            />
            <StatusPill
              testId="ph24-current-workflow-status"
              label="workflow"
              value={body.current_workflow_status || "—"}
              color="slate"
            />
            <StatusPill
              testId="ph24-stalled-workflow-count"
              label="stalled"
              value={body.stalled_workflow_count}
              color={body.stalled_workflow_count > 0 ? "amber" : "slate"}
            />
            <div className="inline-flex items-center gap-1 text-[10px]">
              <span className="text-slate-500">service-level:</span>
              <SLBadge status={sl.status} />
            </div>
            {body.assigned_owner && (
              <StatusPill
                testId="ph24-assigned-owner"
                label="owner"
                value={body.assigned_owner}
                color="slate"
              />
            )}
          </>
        )}

        {kind === "partner" && (
          <>
            <StatusPill
              testId="ph24-partner-open-followup-count"
              label="open follow-ups"
              value={body.open_follow_up_count}
              color={body.open_follow_up_count > 0 ? "cyan" : "slate"}
            />
            <StatusPill
              testId="ph24-partner-stalled-workflow-count"
              label="stalled"
              value={body.stalled_workflow_count}
              color={body.stalled_workflow_count > 0 ? "amber" : "slate"}
            />
            <StatusPill
              testId="ph24-partner-overdue-sl-count"
              label="overdue SL items"
              value={body.overdue_service_level_count}
              color={body.overdue_service_level_count > 0 ? "rose" : "slate"}
            />
          </>
        )}

        {kind === "report" && (
          <>
            <StatusPill
              testId="ph24-report-followup-count"
              label="linked follow-ups"
              value={body.linked_follow_up_count}
              color="slate"
            />
            <StatusPill
              testId="ph24-report-workflow-count"
              label="linked workflow"
              value={body.linked_workflow_count}
              color="slate"
            />
            <StatusPill
              testId="ph24-report-opportunity-count"
              label="linked opportunities"
              value={body.linked_opportunity_count}
              color="slate"
            />
            <div className="inline-flex items-center gap-1 text-[10px]">
              <span className="text-slate-500">service-level:</span>
              <SLBadge status={sl.status} />
            </div>
          </>
        )}

        {body.last_activity_at && (
          <StatusPill
            testId="ph24-last-activity"
            label="last activity"
            value={body.last_activity_at.slice(0, 10)}
            color="slate"
          />
        )}
        {body.last_follow_up_activity && (
          <StatusPill
            testId="ph24-last-followup-activity"
            label="last follow-up"
            value={body.last_follow_up_activity.slice(0, 10)}
            color="slate"
          />
        )}
      </div>

      <div className="mt-2 flex items-center gap-3 flex-wrap text-[10px]">
        {dl.follow_ups && (
          <DeepLink to={dl.follow_ups} testId="ph24-deeplink-follow-ups">
            <ListChecks className="w-3 h-3" /> Follow-ups
          </DeepLink>
        )}
        {dl.opportunity_workflow && (
          <DeepLink to={dl.opportunity_workflow} testId="ph24-deeplink-workflow">
            <Workflow className="w-3 h-3" /> Opportunity workflow
          </DeepLink>
        )}
        {dl.service_levels && (
          <DeepLink to={dl.service_levels} testId="ph24-deeplink-service-levels">
            <Clock className="w-3 h-3" /> Service levels
          </DeepLink>
        )}
        {dl.rankings && (
          <DeepLink to={dl.rankings} testId="ph24-deeplink-rankings">
            <Activity className="w-3 h-3" /> Rankings
          </DeepLink>
        )}
      </div>
    </section>
  );
}


export default PWSStatusPanel;
